<template>
  <Modal
    :show="show"
    :title="$t('workspace.modal.assignIntervenant')"
    size="large"
    :close-on-backdrop-click="!submitting"
    :loading="submitting"
    @close="closeModal"
  >
    <div class="assign-intervenant-modal">
      <!-- Mode Selection -->
      <div class="mode-selection">
        <button
          class="mode-button"
          :class="{ active: mode === 'existing' }"
          @click="mode = 'existing'"
        >
          <span class="icon">👥</span>
          <span>{{ $t('workspace.modal.assignExisting') }}</span>
        </button>
        <button
          class="mode-button"
          :class="{ active: mode === 'new' }"
          @click="mode = 'new'"
        >
          <span class="icon">➕</span>
          <span>{{ $t('workspace.modal.createNew') }}</span>
        </button>
      </div>

      <!-- Existing Intervenant Selection -->
      <div v-if="mode === 'existing'" class="existing-section">
        <!-- Search and Selection Counter -->
        <div class="search-header">
          <div class="search-box">
            <span class="search-icon">🔍</span>
            <input
              v-model="searchQuery"
              type="text"
              :placeholder="$t('workspace.modal.searchIntervenants')"
              class="search-input"
            />
          </div>
          <div v-if="selectedIntervenantIds.length > 0" class="selection-counter">
            {{ $t('workspace.modal.selectedCount', { count: selectedIntervenantIds.length }) }}
            <button class="clear-selection-btn" @click="clearSelection">
              {{ $t('common.clear') }}
            </button>
          </div>
        </div>

        <!-- Loading State -->
        <div v-if="loadingFirmIntervenants" class="loading-state">
          <div class="spinner"></div>
          <p>{{ $t('common.loading') }}</p>
        </div>

        <!-- Intervenant List -->
        <div v-else-if="filteredFirmIntervenants.length" class="intervenant-list">
          <div
            v-for="intervenant in filteredFirmIntervenants"
            :key="intervenant.id"
            class="intervenant-item"
            :class="{ selected: isSelected(intervenant.id) }"
            @click="toggleSelection(intervenant.id)"
          >
            <div class="selection-checkbox">
              <input
                type="checkbox"
                :checked="isSelected(intervenant.id)"
                @click.stop="toggleSelection(intervenant.id)"
              />
            </div>
            <div class="intervenant-avatar" :style="{ backgroundColor: getAvatarColor(intervenant.name) }">
              {{ getInitials(intervenant.name) }}
            </div>
            <div class="intervenant-info">
              <div class="intervenant-name">{{ intervenant.name }}</div>
              <div class="intervenant-details">
                <span class="type-badge">{{ $t(`intervenants.types.${intervenant.type}`) }}</span>
                <span v-if="intervenant.email" class="detail-text">{{ intervenant.email }}</span>
              </div>
            </div>
            <div v-if="isSelected(intervenant.id)" class="checkmark">✓</div>
          </div>
        </div>

        <!-- Empty State -->
        <div v-else class="empty-state">
          <span class="empty-icon">👥</span>
          <p>{{ $t('workspace.modal.noFirmIntervenants') }}</p>
        </div>

        <!-- Actions for Existing -->
        <div class="modal-actions">
          <button type="button" class="btn btn-secondary" @click="closeModal">
            {{ $t('common.cancel') }}
          </button>
          <button
            type="button"
            class="btn btn-primary"
            :disabled="selectedIntervenantIds.length === 0 || submitting"
            @click="handleAssignExisting"
          >
            <span v-if="submitting" class="spinner"></span>
            {{ submitting ? $t('common.assigning') : assignButtonText }}
          </button>
        </div>
      </div>

      <!-- New Intervenant Form -->
      <div v-else class="new-section">
        <form @submit.prevent="handleCreateNew" class="intervenant-form">
          <!-- Intervenant Name -->
          <div class="form-group">
            <label for="intervenantName" class="required">{{ $t('workspace.modal.intervenantName') }}</label>
            <input
              id="intervenantName"
              v-model="form.name"
              type="text"
              :placeholder="$t('workspace.modal.intervenantNamePlaceholder')"
              required
              minlength="2"
              maxlength="100"
            />
            <span v-if="errors.name" class="error-message">{{ errors.name }}</span>
          </div>

          <!-- Type Dropdown -->
          <div class="form-group">
            <label for="intervenantType" class="required">{{ $t('workspace.modal.intervenantType') }}</label>
            <select id="intervenantType" v-model="form.type" required>
              <option value="" disabled>{{ $t('workspace.modal.selectType') }}</option>
              <option value="ARCHITECT">{{ $t('intervenants.types.ARCHITECT') }}</option>
              <option value="CONTRACTOR">{{ $t('intervenants.types.CONTRACTOR') }}</option>
              <option value="CONSULTANT">{{ $t('intervenants.types.CONSULTANT') }}</option>
              <option value="SUPPLIER">{{ $t('intervenants.types.SUPPLIER') }}</option>
              <option value="SUBCONTRACTOR">{{ $t('intervenants.types.SUBCONTRACTOR') }}</option>
              <option value="OTHER">{{ $t('intervenants.types.OTHER') }}</option>
            </select>
            <span v-if="errors.type" class="error-message">{{ errors.type }}</span>
          </div>

          <!-- Email -->
          <div class="form-group">
            <label for="intervenantEmail">{{ $t('workspace.modal.email') }}</label>
            <input
              id="intervenantEmail"
              v-model="form.email"
              type="email"
              :placeholder="$t('workspace.modal.emailPlaceholder')"
              maxlength="100"
            />
            <span v-if="errors.email" class="error-message">{{ errors.email }}</span>
          </div>

          <!-- Phone -->
          <div class="form-group">
            <label for="intervenantPhone">{{ $t('workspace.modal.phone') }}</label>
            <input
              id="intervenantPhone"
              v-model="form.phone"
              type="tel"
              :placeholder="$t('workspace.modal.phonePlaceholder')"
              maxlength="20"
            />
            <span v-if="errors.phone" class="error-message">{{ errors.phone }}</span>
          </div>

          <!-- Address -->
          <div class="form-group">
            <label for="intervenantAddress">{{ $t('workspace.modal.address') }}</label>
            <textarea
              id="intervenantAddress"
              v-model="form.address"
              :placeholder="$t('workspace.modal.addressPlaceholder')"
              maxlength="200"
              rows="3"
            ></textarea>
          </div>

          <!-- Form Actions -->
          <div class="modal-actions">
            <button type="button" class="btn btn-secondary" @click="closeModal">
              {{ $t('common.cancel') }}
            </button>
            <button type="submit" class="btn btn-primary" :disabled="submitting">
              <span v-if="submitting" class="spinner"></span>
              {{ submitting ? $t('common.creating') : $t('common.create') }}
            </button>
          </div>
        </form>
      </div>
    </div>
  </Modal>
</template>

<script setup lang="ts">
// @ts-ignore
import { ref as vueRef, computed as vueComputed, onMounted, watch as vueWatch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useNotifications } from '@/composables/useNotifications'
import Modal from '@/components/base/Modal.vue'
import intervenantsService from '@/services/intervenants.service'
import firmIntervenantsService from '@/services/firmIntervenants.service'
import { getAvatarColor, getInitials } from '@/utils/uiHelpers'
import type { IntervenantData } from '@/types/services'
import type { FirmIntervenantResponse } from '@/services/firmIntervenants.service'

// Aliases to avoid TypeScript errors
const ref = vueRef
const computed = vueComputed
const watch = vueWatch

interface Phase {
  id?: number
  projectId: number
  name: string
  status: 'NOT_STARTED' | 'IN_PROGRESS' | 'ON_HOLD' | 'COMPLETED' | 'CANCELLED'
}

interface Intervenant {
  id?: number
  projectId?: number
  name: string
  type?: string
  email: string
  phone?: string
  address?: string
}

const props = withDefaults(defineProps<{
  show: boolean
  phase: Phase | null
  projectId: number
  existingIntervenants?: Intervenant[]
}>(), {
  existingIntervenants: () => []
})

const emit = defineEmits<{
  close: []
  'intervenant-assigned': []
}>()

const { t } = useI18n()
const { notifySuccess, notifyError } = useNotifications()

// State
const mode = ref<'existing' | 'new'>('existing')
const selectedIntervenantIds = ref<number[]>([])
const searchQuery = ref('')
const loadingFirmIntervenants = ref(false)
const firmIntervenants = ref<FirmIntervenantResponse[]>([])
const submitting = ref(false)

// Form state for new intervenant
const form = ref<IntervenantData>({
  name: '',
  type: 'OTHER',
  email: '',
  phone: '',
  address: ''
})

const errors = ref<Record<string, string>>({})

// Email validation regex
const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

// Phone validation regex (international format)
const phoneRegex = /^[\d\s\-\+\(\)]+$/

// Computed - Get assigned intervenant IDs
const assignedIntervenantIds = computed(() => {
  if (!props.existingIntervenants || !Array.isArray(props.existingIntervenants)) {
    return []
  }
  return props.existingIntervenants.map((i: Intervenant) => i.id).filter((id: number | undefined): id is number => id !== undefined)
})

// Computed - Filter out already assigned intervenants
const availableFirmIntervenants = computed(() => {
  return firmIntervenants.value.filter(
    (intervenant: FirmIntervenantResponse) => !assignedIntervenantIds.value.includes(intervenant.id)
  )
})

// Computed - Filter by search query
const filteredFirmIntervenants = computed(() => {
  if (!searchQuery.value) {
    return availableFirmIntervenants.value
  }
  
  const query = searchQuery.value.toLowerCase()
  return availableFirmIntervenants.value.filter((intervenant: FirmIntervenantResponse) =>
    intervenant.name.toLowerCase().includes(query) ||
    (intervenant.email && intervenant.email.toLowerCase().includes(query)) ||
    (intervenant.phone && intervenant.phone.includes(query))
  )
})

// Computed - Button text based on selection
const assignButtonText = computed(() => {
  const count = selectedIntervenantIds.value.length
  if (count === 0) {
    return t('workspace.modal.assign')
  } else if (count === 1) {
    return t('workspace.modal.assign')
  } else {
    return t('workspace.modal.assignMultiple', { count })
  }
})

// Methods
const isSelected = (id: number): boolean => {
  return selectedIntervenantIds.value.includes(id)
}

const toggleSelection = (id: number) => {
  const index = selectedIntervenantIds.value.indexOf(id)
  if (index > -1) {
    selectedIntervenantIds.value.splice(index, 1)
  } else {
    selectedIntervenantIds.value.push(id)
  }
}

const clearSelection = () => {
  selectedIntervenantIds.value = []
}
const loadFirmIntervenants = async () => {
  loadingFirmIntervenants.value = true
  try {
    const response = await firmIntervenantsService.getFirmIntervenants()
    if (response) {
      firmIntervenants.value = response
    }
  } catch (error) {
    console.error('Error loading firm intervenants:', error)
    notifyError(t('workspace.errors.failedToLoadFirmIntervenants'))
  } finally {
    loadingFirmIntervenants.value = false
  }
}

const validateForm = (): boolean => {
  errors.value = {}
  
  if (!form.value.name || form.value.name.trim().length < 2) {
    errors.value.name = t('workspace.modal.errors.nameRequired')
    return false
  }
  
  if (!form.value.type) {
    errors.value.type = t('workspace.modal.errors.typeRequired')
    return false
  }
  
  if (form.value.email && !emailRegex.test(form.value.email)) {
    errors.value.email = t('workspace.modal.errors.invalidEmail')
    return false
  }
  
  if (form.value.phone && !phoneRegex.test(form.value.phone)) {
    errors.value.phone = t('workspace.modal.errors.invalidPhone')
    return false
  }
  
  return true
}

const handleAssignExisting = async () => {
  if (selectedIntervenantIds.value.length === 0) return
  
  submitting.value = true
  try {
    // Assign all selected intervenants
    const selectedIntervenants = firmIntervenants.value.filter((i: FirmIntervenantResponse) => 
      selectedIntervenantIds.value.includes(i.id)
    )
    
    if (selectedIntervenants.length === 0) {
      throw new Error('Selected intervenants not found')
    }

    // Create all intervenants in parallel
    const assignmentPromises = selectedIntervenants.map((intervenant: FirmIntervenantResponse) =>
      intervenantsService.createIntervenant(props.projectId.toString(), {
        name: intervenant.name,
        type: intervenant.type as any,
        email: intervenant.email || '',
        phone: intervenant.phone || '',
        address: intervenant.address || ''
      })
    )
    
    await Promise.all(assignmentPromises)
    
    const count = selectedIntervenants.length
    if (count === 1) {
      notifySuccess(t('workspace.success.intervenantAssigned'))
    } else {
      notifySuccess(t('workspace.success.intervenantsAssigned', { count }))
    }
    
    emit('intervenant-assigned')
    closeModal()
  } catch (error) {
    console.error('Error assigning intervenants:', error)
    notifyError(t('workspace.errors.intervenantAssignFailed'))
  } finally {
    submitting.value = false
  }
}

const handleCreateNew = async () => {
  if (!validateForm()) return
  
  submitting.value = true
  try {
    await intervenantsService.createIntervenant(props.projectId.toString(), form.value)
    notifySuccess(t('workspace.success.intervenantCreated'))
    emit('intervenant-assigned')
    closeModal()
  } catch (error) {
    console.error('Error creating intervenant:', error)
    notifyError(t('workspace.error.intervenantSaveFailed'))
  } finally {
    submitting.value = false
  }
}

const closeModal = () => {
  mode.value = 'existing'
  selectedIntervenantIds.value = []
  searchQuery.value = ''
  form.value = {
    name: '',
    type: 'OTHER',
    email: '',
    phone: '',
    address: ''
  }
  errors.value = {}
  emit('close')
}

// Watch for modal open
watch(() => props.show, (newValue: boolean) => {
  if (newValue && mode.value === 'existing' && firmIntervenants.value.length === 0) {
    loadFirmIntervenants()
  }
})

// Load on mount if modal is already open
onMounted(() => {
  if (props.show && mode.value === 'existing') {
    loadFirmIntervenants()
  }
})
</script>

<style scoped>
.assign-intervenant-modal {
  padding: 0;
}

/* Mode Selection */
.mode-selection {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
  padding: 1.5rem;
  border-bottom: 1px solid var(--neutral-200);
  background: var(--neutral-50);
}

.mode-button {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  padding: 1rem;
  border: 2px solid var(--neutral-300);
  border-radius: 8px;
  background: white;
  cursor: pointer;
  transition: all 0.2s;
}

.mode-button:hover {
  border-color: var(--primary-400);
  background: var(--primary-50);
}

.mode-button.active {
  border-color: var(--primary-600);
  background: var(--primary-100);
  color: var(--primary-700);
}

.mode-button .icon {
  font-size: 1.5rem;
}

/* Existing Section */
.existing-section {
  display: flex;
  flex-direction: column;
  min-height: 400px;
}

/* Search Header */
.search-header {
  padding: 1rem 1.5rem;
  border-bottom: 1px solid var(--neutral-200);
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.search-box {
  position: relative;
  flex: 1;
}

.search-icon {
  position: absolute;
  left: 0.75rem;
  top: 50%;
  transform: translateY(-50%);
  color: var(--neutral-500);
  font-size: 0.875rem;
}

.search-input {
  width: 100%;
  padding: 0.75rem 0.75rem 0.75rem 2.25rem;
  border: 1px solid var(--neutral-300);
  border-radius: 8px;
  font-size: 0.875rem;
}

.search-input:focus {
  outline: none;
  border-color: var(--primary-500);
  box-shadow: 0 0 0 3px var(--primary-100);
}

/* Selection Counter */
.selection-counter {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.5rem 1rem;
  background: var(--primary-50);
  border-radius: 6px;
  font-size: 0.875rem;
  color: var(--primary-700);
  font-weight: 500;
}

.clear-selection-btn {
  background: none;
  border: none;
  color: var(--primary-600);
  text-decoration: underline;
  cursor: pointer;
  font-size: 0.875rem;
  padding: 0;
  margin-left: 1rem;
}

.clear-selection-btn:hover {
  color: var(--primary-800);
}

/* Loading State */
.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem;
  gap: 1rem;
}

/* Intervenant List */
.intervenant-list {
  flex: 1;
  overflow-y: auto;
  max-height: 400px;
  padding: 1rem;
}

.intervenant-item {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem;
  border: 2px solid var(--neutral-200);
  border-radius: 8px;
  margin-bottom: 0.75rem;
  cursor: pointer;
  transition: all 0.2s;
}

.intervenant-item:hover {
  border-color: var(--primary-300);
  background: var(--primary-50);
}

.intervenant-item.selected {
  border-color: var(--primary-600);
  background: var(--primary-100);
}

/* Selection Checkbox */
.selection-checkbox {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.selection-checkbox input[type="checkbox"] {
  width: 1.25rem;
  height: 1.25rem;
  cursor: pointer;
  accent-color: var(--primary-600);
}

.intervenant-avatar {
  width: 3rem;
  height: 3rem;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: 600;
  font-size: 1rem;
  flex-shrink: 0;
}

.intervenant-info {
  flex: 1;
}

.intervenant-name {
  font-weight: 600;
  color: var(--neutral-900);
  margin-bottom: 0.25rem;
}

.intervenant-details {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-wrap: wrap;
}

.type-badge {
  padding: 0.25rem 0.75rem;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 600;
  background: var(--primary-100);
  color: var(--primary-700);
}

.detail-text {
  font-size: 0.875rem;
  color: var(--neutral-600);
}

.checkmark {
  width: 1.5rem;
  height: 1.5rem;
  border-radius: 50%;
  background: var(--primary-600);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
}

/* Empty State */
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem;
  text-align: center;
  color: var(--neutral-500);
}

.empty-icon {
  font-size: 3rem;
  margin-bottom: 1rem;
  opacity: 0.5;
}

/* New Section */
.new-section {
  padding: 0;
}

.intervenant-form {
  display: flex;
  flex-direction: column;
}

.form-group {
  margin-bottom: 1.5rem;
  padding: 0 1.5rem;
}

.form-group label {
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 500;
  color: var(--neutral-700);
}

.form-group label.required::after {
  content: ' *';
  color: var(--error-600);
}

.form-group input,
.form-group textarea,
.form-group select {
  width: 100%;
  padding: 0.75rem;
  border: 1px solid var(--neutral-300);
  border-radius: 8px;
  font-size: 0.875rem;
  transition: border-color 0.15s;
}

.form-group input:focus,
.form-group textarea:focus,
.form-group select:focus {
  outline: none;
  border-color: var(--primary-500);
  box-shadow: 0 0 0 3px var(--primary-100);
}

.error-message {
  display: block;
  margin-top: 0.5rem;
  font-size: 0.875rem;
  color: var(--error-600);
}

/* Modal Actions */
.modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
  padding: 1.5rem;
  border-top: 1px solid var(--neutral-200);
  background: var(--neutral-50);
  margin-top: auto;
}

.btn {
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s;
  border: none;
  font-size: 0.875rem;
}

.btn-secondary {
  background: var(--neutral-100);
  color: var(--neutral-700);
}

.btn-secondary:hover {
  background: var(--neutral-200);
}

.btn-primary {
  background: var(--primary-600);
  color: white;
}

.btn-primary:hover:not(:disabled) {
  background: var(--primary-700);
}

.btn-primary:disabled {
  background: var(--neutral-300);
  color: var(--neutral-500);
  cursor: not-allowed;
}

.spinner {
  display: inline-block;
  width: 14px;
  height: 14px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top-color: white;
  border-radius: 50%;
  animation: spin 0.6s linear infinite;
  margin-right: 0.5rem;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

/* Responsive */
@media (max-width: 768px) {
  .mode-selection {
    grid-template-columns: 1fr;
  }
  
  .intervenant-list {
    max-height: 300px;
  }
  
  .intervenant-item {
    padding: 0.75rem;
  }
  
  .intervenant-avatar {
    width: 2.5rem;
    height: 2.5rem;
    font-size: 0.875rem;
  }
}
</style>